import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import { ChecklistDisplay } from '../../Common/TaskChecklist';
import 'react-datepicker/dist/react-datepicker.css';
import './AdminArchives.css';
import '../CleanerDashboard/AvailableTasks.css';

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ZIP_CODE_PATTERN = /^\d{3,10}(?:[-\s]\d{2,6})?$/;
const CITY_PATTERN = /^[A-Za-z\s.'-]+$/;

const stripTrailingCityZip = (value) => {
  const address = String(value || '').trim();
  if (!address) return '';

  let cleaned = address.replace(/,\s*[^,]+?\s+\d{3,10}(?:[-\s]\d{2,6})?\s*$/i, '').trim();
  const parts = cleaned.split(',').map((part) => part.trim()).filter(Boolean);

  if (parts.length >= 3) {
    const lastPart = parts[parts.length - 1];
    const secondLastPart = parts[parts.length - 2];

    if (ZIP_CODE_PATTERN.test(lastPart)) {
      parts.pop();
      if (CITY_PATTERN.test(secondLastPart)) {
        parts.pop();
      }
      cleaned = parts.join(', ');
    }
  }

  return cleaned.replace(/[\s,]+$/, '').trim() || address;
};

const getListAddress = (task) => {
  const rawAddress = String(task?.address || '').trim();
  if (!rawAddress) return '—';

  let cleanedAddress = rawAddress;
  const city = String(task?.client?.city || task?.city || '').trim();
  const zipCode = String(task?.client?.zipCode || task?.zipCode || '').trim();

  if (zipCode) {
    cleanedAddress = cleanedAddress.replace(new RegExp(`,?\\s*${escapeRegExp(zipCode)}\\s*$`, 'i'), '');
  }
  if (city) {
    cleanedAddress = cleanedAddress.replace(new RegExp(`,?\\s*${escapeRegExp(city)}\\s*$`, 'i'), '');
  }

  cleanedAddress = cleanedAddress.replace(/[\s,]+$/, '').trim();
  if (!cleanedAddress || cleanedAddress === rawAddress) {
    cleanedAddress = stripTrailingCityZip(rawAddress);
  }

  return cleanedAddress || rawAddress;
};

const AdminArchives = () => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [completedTasks, setCompletedTasks] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [ratings, setRatings] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedClientFilter, setSelectedClientFilter] = useState('');
  const [selectedCleanerFilter, setSelectedCleanerFilter] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [clients, setClients] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [totalHours, setTotalHours] = useState(0);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedPersonType, setSelectedPersonType] = useState(null); // 'cleaner' or 'client'

  // Helper function to get sequential task number (1, 2, 3...)
  const getTaskIdNumber = (taskId, index) => {
    return index + 1;
  };

  // Helper function to format date as dd,MM,yyyy
  const formatDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day},${month},${year}`;
  };

  useEffect(() => {
    fetchClients();
    fetchCleaners();
  }, []);

  useEffect(() => {
    fetchCompletedTasks();
  }, [startDate, endDate, selectedClientFilter, selectedCleanerFilter, taskIdFilter]);

  const fetchCompletedTasks = async () => {
    try {
      const params = {};
      
      // Add date filters
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (selectedClientFilter) params.clientId = selectedClientFilter;
      if (selectedCleanerFilter) params.cleanerId = selectedCleanerFilter;
      
      const response = await api.get('/tasks/completed', { params });
      let tasks = response.data.data;
      
      // Debug: Log task structure to see associations
      if (tasks.length > 0 && process.env.NODE_ENV === 'development') {
        console.log('Sample task structure:', tasks[0]);
        console.log('Client association:', tasks[0].client);
        console.log('Cleaner association:', tasks[0].cleaner);
        console.log('ClientId:', tasks[0].clientId);
        console.log('CleanerId:', tasks[0].cleanerId);
      }
      
      // Apply Task ID filter on frontend if provided
      if (taskIdFilter) {
        tasks = tasks.filter(task => {
          const taskId = (task._id || task.id || '').toString().toLowerCase();
          return taskId.includes(taskIdFilter.toLowerCase());
        });
      }
      
      // Sort by date descending (latest first), then by createdAt
      tasks = (tasks || []).sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setCompletedTasks(tasks);
      
      // Calculate total hours for selected cleaner or client
      let total = 0;
      let person = null;
      let personType = null;
      
      if (selectedCleanerFilter) {
        const cleaner = cleaners.find(c => {
          const cleanerId = String(c._id || c.id || '');
          const filterValue = String(selectedCleanerFilter);
          return cleanerId === filterValue;
        });
        if (cleaner) {
          person = cleaner;
          personType = 'cleaner';
          total = tasks.reduce((sum, task) => {
            const hours = parseFloat(task.actualHours || task.hours || 0);
            return sum + hours;
          }, 0);
        }
      } else if (selectedClientFilter) {
        const client = clients.find(c => {
          const clientId = String(c._id || c.id || '');
          const clientEmail = String(c.email || '');
          const filterValue = String(selectedClientFilter);
          return clientId === filterValue || clientEmail === filterValue;
        });
        if (client) {
          person = client;
          personType = 'client';
          total = tasks.reduce((sum, task) => {
            const hours = parseFloat(task.actualHours || task.hours || 0);
            return sum + hours;
          }, 0);
        }
      }
      
      setTotalHours(total);
      setSelectedPerson(person);
      setSelectedPersonType(personType);
      
      // Fetch ratings for each task
      const ratingsData = {};
      for (const task of tasks) {
        if (task.rating) {
          ratingsData[task._id || task.id] = {
            rating: task.rating,
            comment: task.ratingComment
          };
        }
      }
      setRatings(ratingsData);
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
      showToast(error.response?.data?.message || t('admin.errorFetchingTasks', 'Error fetching completed tasks. Please try again.'), 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCleaners = async () => {
    try {
      const response = await api.get('/users', { params: { role: 'cleaner' } });
      const cleaners = response.data.data.map(user => ({
        _id: user.id || user._id,
        id: user.id || user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        experience: user.experience || 0
      }));
      setCleaners(cleaners);
    } catch (error) {
      console.error('Error fetching cleaners:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/users', { params: { role: 'client' } });
      const clients = response.data.data.map(user => ({
        _id: user.id || user._id,
        id: user.id || user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        address: user.address
      }));
      setClients(clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  if (initialLoading) {
    return <div className="loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="admin-archives">
      <div className="admin-archives-header">
        <h2>{t('admin.completedTasksArchive', 'Completed Tasks Archive')}</h2>
        
        {/* Total Hours Summary */}
        {(selectedCleanerFilter || selectedClientFilter) && selectedPerson && (
          <div className="hours-summary">
            <div className="hours-summary-card">
              <div className="hours-summary-header">
                <h3>
                  {selectedPersonType === 'cleaner' 
                    ? t('admin.totalHoursForCleaner', 'Total Hours for Cleaner')
                    : t('admin.totalHoursForClient', 'Total Hours for Client')}
                </h3>
              </div>
              <div className="hours-summary-content">
                <div className="person-info">
                  <span className="person-name">{selectedPerson.name}</span>
                  {selectedPerson.email && (
                    <span className="person-email">({selectedPerson.email})</span>
                  )}
                </div>
                <div className="hours-total">
                  <span className="hours-label">{t('admin.totalHours', 'Total Hours')}:</span>
                  <span className="hours-value">{totalHours.toFixed(2)} {t('admin.hours', 'hours')}</span>
                </div>
                {(startDate || endDate) && (
                  <div className="date-range-info">
                    {startDate && endDate && (
                      <span>
                        {t('common.from', 'From')} {formatDate(startDate)} 
                        {' '}{t('common.to', 'To')}{' '} 
                        {formatDate(endDate)}
                      </span>
                    )}
                    {startDate && !endDate && (
                      <span>
                        {t('common.from', 'From')} {formatDate(startDate)}
                      </span>
                    )}
                    {!startDate && endDate && (
                      <span>
                        {t('admin.until', 'Until')} {formatDate(endDate)}
                      </span>
                    )}
                  </div>
                )}
                {!startDate && !endDate && (
                  <div className="date-range-info">
                    <span>{t('admin.allTime', 'All Time')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="filters-container">
          <div className="date-filter">
            <div className="date-filter-group">
              <label>{t('common.from', 'From')}:</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText={t('common.startDate', 'Start date')}
                dateFormat="dd,MM,yyyy"
                className="date-picker-input"
              />
            </div>
            <div className="date-filter-group">
              <label>{t('common.to', 'To')}:</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText={t('common.endDate', 'End date')}
                dateFormat="dd,MM,yyyy"
                className="date-picker-input"
              />
            </div>
          </div>
          <div className="filter-group">
            <label>{t('admin.filterByClient', 'Filter by Client')}:</label>
            <select
              value={selectedClientFilter}
              onChange={(e) => setSelectedClientFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('admin.allClientsFilter', 'All Clients')}</option>
              {clients.map(client => (
                <option key={client._id || client.id || client.email} value={client._id || client.id || client.email}>
                  {client.name} {client.email ? `(${client.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('admin.filterByCleaner', 'Filter by Cleaner')}:</label>
            <select
              value={selectedCleanerFilter}
              onChange={(e) => setSelectedCleanerFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('admin.allCleanersFilter', 'All Cleaners')}</option>
              {cleaners.map(cleaner => (
                <option key={cleaner._id || cleaner.id} value={cleaner._id || cleaner.id}>
                  {cleaner.name} {cleaner.email ? `(${cleaner.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('admin.searchByTaskId', 'Search by Task ID')}:</label>
            <input
              type="text"
              value={taskIdFilter}
              onChange={(e) => setTaskIdFilter(e.target.value)}
              placeholder={t('admin.searchByTaskIdPlaceholder', 'e.g., task-1, completed-2')}
              className="filter-select"
            />
          </div>
          {(startDate || endDate || selectedClientFilter || selectedCleanerFilter || taskIdFilter) && (
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                setSelectedClientFilter('');
                setSelectedCleanerFilter('');
                setTaskIdFilter('');
              }}
            >
              {t('admin.clearAllFilters', 'Clear All Filters')}
            </button>
          )}
        </div>
      </div>
      
      {completedTasks.length === 0 ? (
        <p className="no-tasks">{t('admin.noCompletedTasks', 'No completed tasks yet.')}</p>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="archives-table">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.taskId', 'Task ID')}</th>
                  <th>{t('admin.client', 'Client')}</th>
                  <th>{t('auth.address', 'Address')}</th>
                  <th>{t('client.date', 'Date')}</th>
                  <th>{t('admin.cleanerName', 'Cleaner')}</th>
                  <th>{t('admin.hoursWorked', 'Hours Worked')}</th>
                  <th>{t('admin.rating', 'Rating')}</th>
                  <th>{t('admin.clientComment', 'Client Comment')}</th>
                  <th>{t('admin.action', 'Action')}</th>
                </tr>
              </thead>
              <tbody>
                {completedTasks.map((task, index) => {
                  const taskId = task._id || task.id;
                  // Sequelize returns associations as 'client' and 'cleaner' objects
                  // If association is null, try to find from clients/cleaners list
                  let client = task.client;
                  if (!client && task.clientId) {
                    client = clients.find(c => (c._id || c.id) === task.clientId);
                  }
                  let cleaner = task.cleaner;
                  if (!cleaner && task.cleanerId) {
                    cleaner = cleaners.find(c => (c._id || c.id) === task.cleanerId);
                  }
                  return (
                    <tr key={taskId}>
                      <td>{getTaskIdNumber(taskId, index)}</td>
                      <td>{client?.name || 'N/A'}</td>
                      <td>{getListAddress(task)}</td>
                      <td>{new Date(task.date).toLocaleDateString('en-GB')}</td>
                      <td>{cleaner?.name || 'N/A'}</td>
                      <td>{task.actualHours || task.hours}</td>
                      <td>
                        {ratings[taskId]?.rating ? (
                          <span className="rating-display">
                            {'★'.repeat(ratings[taskId].rating)}
                            {'☆'.repeat(5 - ratings[taskId].rating)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{ratings[taskId]?.comment || '-'}</td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedTask(task)}
                        >
                          {t('admin.details', 'Details')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="archives-cards">
            {completedTasks.map((task, index) => {
              const taskId = task._id || task.id;
              // Sequelize returns associations as 'client' and 'cleaner' objects
              // If association is null, try to find from clients/cleaners list
              let client = task.client;
              if (!client && task.clientId) {
                client = clients.find(c => (c._id || c.id) === task.clientId);
              }
              let cleaner = task.cleaner;
              if (!cleaner && task.cleanerId) {
                cleaner = cleaners.find(c => (c._id || c.id) === task.cleanerId);
              }
              return (
                <div key={taskId} className="task-card">
                  <div className="task-card-header">
                    <span className="task-card-id">
                      {t('admin.taskId', 'Task ID')}: {getTaskIdNumber(taskId, index)}
                    </span>
                    {ratings[taskId]?.rating && (
                      <span className="rating-display">
                        {'★'.repeat(ratings[taskId].rating)}
                        {'☆'.repeat(5 - ratings[taskId].rating)}
                      </span>
                    )}
                  </div>
                  <div className="task-card-details">
                    <div className="task-card-row">
                      <span className="task-card-label">{t('admin.client', 'Client')}</span>
                      <span className="task-card-value">{client?.name || 'N/A'}</span>
                    </div>
                    <div className="task-card-row">
                      <span className="task-card-label">{t('auth.address', 'Address')}</span>
                      <span className="task-card-value">{getListAddress(task)}</span>
                    </div>
                    <div className="task-card-row">
                      <span className="task-card-label">{t('client.date', 'Date')}</span>
                      <span className="task-card-value">{new Date(task.date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="task-card-row">
                      <span className="task-card-label">{t('admin.cleanerName', 'Cleaner')}</span>
                      <span className="task-card-value">{cleaner?.name || 'N/A'}</span>
                    </div>
                    <div className="task-card-row">
                      <span className="task-card-label">{t('admin.hoursWorked', 'Hours Worked')}</span>
                      <span className="task-card-value">{task.actualHours || task.hours}</span>
                    </div>
                    {ratings[taskId]?.comment && (
                      <div className="task-card-row">
                        <span className="task-card-label">{t('admin.clientComment', 'Client Comment')}</span>
                        <span className="task-card-value">{ratings[taskId].comment}</span>
                      </div>
                    )}
                  </div>
                  <div className="task-card-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setSelectedTask(task)}
                    >
                      {t('admin.details', 'Details')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {selectedTask && (
        <div className="modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admin.completedTaskDetails', 'Completed Task Details')}</h3>
              <button className="close-btn" onClick={() => setSelectedTask(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Status Badge */}
              <div className="at-detail-status">
                <span className={`status status-completed`} style={{ padding: '6px 18px', borderRadius: '20px', fontSize: '14px', fontWeight: 700, textTransform: 'capitalize', background: '#d1fae5', color: '#065f46' }}>
                  Completed
                </span>
              </div>

              {/* Client Info */}
              <div className="at-detail-section">
                <h4>{t('admin.clientInformation', 'Client Information')}</h4>
                {(() => {
                  let client = selectedTask.client;
                  if (!client && selectedTask.clientId) {
                    client = clients.find(c => (c._id || c.id) === selectedTask.clientId);
                  }
                  return (
                    <>
                      <div className="at-detail-row">
                        <span className="at-detail-icon">👤</span>
                        <span><strong>{t('admin.client', 'Client')}:</strong> {client?.name || 'N/A'}</span>
                      </div>
                      <div className="at-detail-row">
                        <span className="at-detail-icon">🏙️</span>
                        <span><strong>{t('auth.city', 'City')}:</strong> {client?.city || '-'}</span>
                      </div>
                      <div className="at-detail-row">
                        <span className="at-detail-icon">📮</span>
                        <span><strong>{t('auth.zipCode', 'Zip Code')}:</strong> {client?.zipCode || '-'}</span>
                      </div>
                      {client?.email && (
                        <div className="at-detail-row">
                          <span className="at-detail-icon">📧</span>
                          <span><strong>{t('auth.email', 'Email')}:</strong> {client.email}</span>
                        </div>
                      )}
                      {client?.phone && (
                        <div className="at-detail-row">
                          <span className="at-detail-icon">📞</span>
                          <span><strong>{t('auth.phone', 'Phone')}:</strong> {client.phone}</span>
                        </div>
                      )}
                      <div className="at-detail-row">
                        <span className="at-detail-icon">📍</span>
                        <span><strong>{t('auth.address', 'Address')}:</strong> {selectedTask.address}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Cleaner Info */}
              <div className="at-detail-section">
                <h4>{t('admin.cleanerInformation', 'Cleaner Information')}</h4>
                {(() => {
                  let cleaner = selectedTask.cleaner;
                  if (!cleaner && selectedTask.cleanerId) {
                    cleaner = cleaners.find(c => (c._id || c.id) === selectedTask.cleanerId);
                  }
                  return (
                    <>
                      <div className="at-detail-row">
                        <span className="at-detail-icon">🧹</span>
                        <span><strong>{t('admin.cleanerName', 'Cleaner')}:</strong> {cleaner?.name || 'N/A'}</span>
                      </div>
                      {cleaner?.email && (
                        <div className="at-detail-row">
                          <span className="at-detail-icon">📧</span>
                          <span><strong>{t('auth.email', 'Email')}:</strong> {cleaner.email}</span>
                        </div>
                      )}
                      {cleaner?.phone && (
                        <div className="at-detail-row">
                          <span className="at-detail-icon">📞</span>
                          <span><strong>{t('auth.phone', 'Phone')}:</strong> {cleaner.phone}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Booking Details */}
              <div className="at-detail-section">
                <h4>{t('admin.bookingDetails', 'Booking Details')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🆔</span>
                  <span><strong>{t('admin.taskId', 'Task ID')}:</strong> {completedTasks.findIndex(t => (t._id || t.id) === (selectedTask._id || selectedTask.id)) + 1}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📅</span>
                  <span><strong>{t('client.date', 'Date')}:</strong> {new Date(selectedTask.date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏰</span>
                  <span><strong>{t('client.time', 'Time')}:</strong> {selectedTask.time || 'N/A'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🏠</span>
                  <span><strong>{t('admin.type', 'Cleaning Type')}:</strong> {selectedTask.cleaningType || 'N/A'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏱️</span>
                  <span><strong>{t('admin.hoursWorked', 'Hours Worked')}:</strong> {selectedTask.actualHours || selectedTask.hours || 'N/A'}</span>
                </div>
              </div>

              {/* Checklist */}
              {selectedTask.checklist && selectedTask.checklist.length > 0 && (
                <div className="at-detail-section">
                  <h4>{t('client.whatNeedsToBeDone', 'Checklist')}</h4>
                  <ChecklistDisplay items={selectedTask.checklist} completed={selectedTask.checklistCompleted} />
                </div>
              )}

              {/* Comments */}
              {selectedTask.comments && (
                <div className="at-detail-section">
                  <h4>{t('admin.clientsSpecialInstructions', `Client's Special Instructions / Comments`)}</h4>
                  <div className="at-detail-comments">{selectedTask.comments}</div>
                </div>
              )}

              {/* Rating */}
              {(() => {
                const taskId = selectedTask._id || selectedTask.id;
                return ratings[taskId] && (
                  <div className="at-detail-section">
                    <h4>{t('admin.rating', 'Rating & Review')}</h4>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">⭐</span>
                      <span>
                        <strong>{t('admin.rating', 'Rating')}:</strong> 
                        <span style={{ marginLeft: '10px' }}>
                          {'★'.repeat(ratings[taskId].rating)}
                          {'☆'.repeat(5 - ratings[taskId].rating)}
                          <span style={{ marginLeft: '8px', color: '#4a5568', fontWeight: 600 }}>
                            {ratings[taskId].rating}/5
                          </span>
                        </span>
                      </span>
                    </div>
                    {ratings[taskId].comment && (
                      <div className="at-detail-row" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                        <span className="at-detail-icon">💬</span>
                        <span><strong>{t('admin.clientComment', 'Client Comment')}:</strong></span>
                      </div>
                    )}
                    {ratings[taskId].comment && (
                      <div className="at-detail-comments">{ratings[taskId].comment}</div>
                    )}
                  </div>
                );
              })()}

              {/* Close Button */}
              <div className="at-modal-actions" style={{ padding: '16px 0 0', borderTop: '1px solid #e2e8f0' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedTask(null)}
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArchives;



